import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  IconButton,
  Progress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Image,
  Center,
  Flex
} from '@chakra-ui/react';
import { FiArrowLeft, FiX, FiPlus } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';
import { uploadFileDirect, createContentDocumentDirect } from '../services/uploadService';

// Removed chapter management for books per client requirement

const UploadBook: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author: '',
    genres: [] as string[],
    rating: 'All Ages' as string,
    language: 'English',
    tags: '',
    cast: '',
    director: '',
    writer: '',
    coverFile: null as File | null,
    coverUrl: '',
    bookFile: null as File | null,
    bookFileName: ''
  });
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre: string) => {
    const isSelected = formData.genres.includes(genre);
    
    if (isSelected) {
      handleInputChange('genres', formData.genres.filter(g => g !== genre));
    } else if (formData.genres.length < 3) {
      handleInputChange('genres', [...formData.genres, genre]);
    }
  };

  const handleCoverSelect = (file: File) => {
    handleInputChange('coverFile', file);
    const url = URL.createObjectURL(file);
    handleInputChange('coverUrl', url);
  };

  const handleCoverRemove = () => {
    handleInputChange('coverFile', null);
    handleInputChange('coverUrl', '');
  };

  const handleBookSelect = (file: File) => {
    handleInputChange('bookFile', file);
    handleInputChange('bookFileName', file.name);
  };

  const handleBookRemove = () => {
    handleInputChange('bookFile', null);
    handleInputChange('bookFileName', '');
  };

  // Chapter management removed

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.title.trim()) {
        toast({
        title: 'Title required',
        description: 'Please enter a book title.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (!formData.description.trim()) {
        toast({
        title: 'Description required',
        description: 'Please enter a book description.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (formData.genres.length === 0) {
        toast({
          title: 'Genre required',
          description: 'Please select at least one genre.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    setCurrentStep(2);
  };

  const prevStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!user) return;

    if (!formData.bookFile) {
      toast({
        title: 'Book file required',
        description: 'Please upload a PDF file.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload novel cover
      let coverUrl = '';
      if (formData.coverFile) {
        coverUrl = await uploadFileDirect(`books/${user.uid}/covers`, formData.coverFile);
        setUploadProgress(20);
      }

      // Upload main book file
      let bookFileUrl = '';
      if (formData.bookFile) {
        bookFileUrl = await uploadFileDirect(`books/${user.uid}/files`, formData.bookFile);
        setUploadProgress(60);
      }

      // Create book document
      const bookData = {
        title: formData.title,
        description: formData.description,
        category: 'book',
        visibility: 'public',
        rating: formData.rating,
        language: formData.language,
        genres: formData.genres,
        tags: formData.tags || '',
        cast: formData.cast.split(',').map(name => name.trim()),
        director: formData.director.split(',').map(name => name.trim()),
        writer: formData.writer.split(',').map(name => name.trim()),
        author: formData.author,
        seasonNumber: '',
        episodeNumber: '',
        chapterNumber: ''
      };

      const assetUrls: { [key: string]: string } = {};
      if (coverUrl) assetUrls.poster = coverUrl;
      if (bookFileUrl) assetUrls.bookFile = bookFileUrl;

      const bookId = await createContentDocumentDirect(user.uid, bookData, assetUrls);
      setUploadProgress(90);

      // Finalize
      setUploadProgress(100);

      toast({
        title: 'Book uploaded successfully!',
        description: 'Your book has been published.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate('/');
    } catch (error) {
      console.error('Error uploading book:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your book. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const renderStep1 = () => (
    <VStack gap={6} align="stretch">
      {/* Title */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Book Title *
        </FormLabel>
        <Input
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Enter book title"
          bg={inputBg}
          borderColor={borderColor}
        />
      </FormControl>

      {/* Description */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Description *
        </FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter book description"
          bg={inputBg}
          borderColor={borderColor}
          rows={4}
        />
      </FormControl>

      {/* Author */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Author
        </FormLabel>
        <Input
          value={formData.author}
          onChange={(e) => handleInputChange('author', e.target.value)}
          placeholder="Enter author name"
          bg={inputBg}
          borderColor={borderColor}
        />
      </FormControl>

      {/* Genres */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Genres * (Select up to 3)
        </FormLabel>
        <Flex wrap="wrap" gap={2}>
          {GENRES.map(genre => (
            <Button
              key={genre}
              size="sm"
              variant={formData.genres.includes(genre) ? 'solid' : 'outline'}
              colorScheme="purple"
              onClick={() => handleGenreToggle(genre)}
              isDisabled={!formData.genres.includes(genre) && formData.genres.length >= 3}
            >
              {genre}
            </Button>
          ))}
        </Flex>
      </FormControl>

      {/* Cast, Director, Writer */}
      <HStack gap={4} align="start">
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Characters
          </FormLabel>
          <Input
            value={formData.cast}
            onChange={(e) => handleInputChange('cast', e.target.value)}
            placeholder="Character names (comma separated)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Editor
          </FormLabel>
          <Input
            value={formData.director}
            onChange={(e) => handleInputChange('director', e.target.value)}
            placeholder="Editor name(s)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Writer
          </FormLabel>
          <Input
            value={formData.writer}
            onChange={(e) => handleInputChange('writer', e.target.value)}
            placeholder="Writer name(s)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
      </HStack>

      {/* Content Rating */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Content Rating
        </FormLabel>
        <RadioGroup
          value={formData.rating}
          onChange={(value) => handleInputChange('rating', value)}
        >
          <HStack gap={4} wrap="wrap">
            {CONTENT_RATINGS.map(rating => (
              <Radio key={rating.value} value={rating.value} colorScheme="purple">
                {rating.label}
              </Radio>
            ))}
          </HStack>
        </RadioGroup>
      </FormControl>
    </VStack>
  );

  const renderStep2 = () => (
    <VStack gap={6} align="stretch">
      {/* Book Cover */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Book Cover
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={formData.coverFile ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={8}
          textAlign="center"
          bg={formData.coverFile ? 'purple.50' : inputBg}
          _dark={{ bg: formData.coverFile ? 'purple.900' : 'gray.700' }}
          cursor="pointer"
          _hover={{ borderColor: 'purple.400' }}
          transition="all 0.2s"
          onClick={() => document.getElementById('cover-upload')?.click()}
          minH="200px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {formData.coverFile ? (
            <VStack gap={3}>
              <Box position="relative">
                <Image
                  src={formData.coverUrl!}
                  alt="Cover preview"
                  maxH="150px"
                  maxW="200px"
                  objectFit="cover"
                  borderRadius="md"
                />
                <Button
                  position="absolute"
                  top={-2}
                  right={-2}
                  size="sm"
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCoverRemove();
                  }}
                >
                  <IconWrapper icon={FiX} size={12} />
                </Button>
              </Box>
            </VStack>
          ) : (
            <VStack gap={2}>
              <IconWrapper icon={FiPlus} size={32} color="gray.400" />
              <Text color="gray.500">Click to upload cover</Text>
            </VStack>
          )}
          <Input
            id="cover-upload"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCoverSelect(file);
            }}
            display="none"
          />
        </Box>
      </FormControl>

      {/* Book File */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Book File * (PDF only)
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={formData.bookFile ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={6}
          bg={formData.bookFile ? 'purple.50' : inputBg}
          _dark={{ bg: formData.bookFile ? 'purple.900' : 'gray.700' }}
        >
          <HStack justify="space-between" align="center" w="100%">
            <Text color={labelColor} noOfLines={1} maxW="70%">
              {formData.bookFileName || 'No file selected'}
            </Text>
            <HStack>
              {formData.bookFile ? (
                <Button size="sm" colorScheme="red" variant="outline" onClick={handleBookRemove}>
                  Remove
                </Button>
              ) : (
                <Button size="sm" colorScheme="purple" onClick={() => document.getElementById('book-file-upload')?.click()}>
                  Select File
                </Button>
              )}
            </HStack>
          </HStack>
          <Input
            id="book-file-upload"
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleBookSelect(file);
            }}
            display="none"
          />
        </Box>
      </FormControl>

      {/* Chapter management removed per client request */}
    </VStack>
  );

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to upload books.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <HStack gap={4} align="center" maxW="1200px" mx="auto">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate('/upload')}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <VStack align="start" gap={1}>
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Upload Book
            </Text>
            <Text color={labelColor}>
              Step {currentStep} of 2
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="1000px" mx="auto">
        {currentStep === 1 ? renderStep1() : renderStep2()}

        {/* Progress Bar */}
        {isUploading && (
          <VStack gap={2} mt={6}>
            <Text fontSize="sm" color={labelColor}>
              Uploading... {Math.round(uploadProgress)}%
            </Text>
            <Progress
              value={uploadProgress}
              size="sm"
              colorScheme="purple"
              w="100%"
              borderRadius="full"
            />
          </VStack>
        )}

        {/* Navigation Buttons */}
        <HStack gap={4} mt={8}>
          {currentStep > 1 && (
            <Button
              variant="outline"
              colorScheme="purple"
              onClick={prevStep}
              flex="1"
            >
              Back
            </Button>
          )}
          <Button
            colorScheme="purple"
            onClick={currentStep === 2 ? handleSubmit : nextStep}
            isLoading={isUploading}
            loadingText="Uploading..."
            flex="1"
          >
            {currentStep === 2 ? 'Upload Book' : 'Continue'}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default UploadBook;
